/* @flow */

import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { head, get, keyBy, toString } from 'lodash/fp';
import round from 'lodash/round';
import { useDropzone } from 'react-dropzone';
import download from 'downloadjs';
import { parse, unparse } from 'papaparse';
import styled from '@emotion/styled';

import { isChallengeAFL, isChallengeNBA } from 'app/utils/challengeUtils';
import Divider from 'app/components/commons/Divider';
import NotificationHandler from 'app/components/notifications/NotificationHandler';
import FormValidationMessage from 'app/components/commons/FormValidationMessage';
import { updatePlayers } from 'app/actions/challengeActions';
import { setSortField } from 'app/actions/playerListActions';
import entitiesSelector from 'app/selectors/entitiesSelector';
import Button from 'app/components/commons/Button';
import Columns from 'app/components/commons/Columns';
import Column from 'app/components/commons/Column';
import { fileToText } from 'app/utils/fileUtils';

type Props = {
  onSetSortField: (field: string) => void,
  onUpdatePlayers: (players: Object) => void,
  sortField: string,
  sortDirection: 'asc' | 'desc',
  entities: Array<Object>,
  isAFL?: boolean,
  isNBA?: boolean,
  isNativeWebView: boolean,
  useAverage?: boolean
};

const defaultProps = {
  isAFL: false,
  isNBA: false,
  useAverage: false
};

const StyledDropzone = styled.div(({ isDragActive, theme }) => ({
  borderColor: isDragActive ? theme.color.primary : theme.color.basis4,
  borderRadius: 5,
  borderStyle: isDragActive ? 'solid' : 'dashed',
  padding: theme.spacing.small,
  marginBottom: theme.spacing.small,
  textAlign: 'center'
}));

const DropZoneTextContainer = styled.div({
  flex: 3
});

const DropZoneMainText = styled.span(props => ({
  color: props.theme.color.basis4
}));

const ExtensionLabel = styled.span({
  paddingLeft: 6
});

const DropzoneContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const DropZoneRejectedText = styled.span(({ theme }) => ({
  color: theme.color.danger
}));

const getHeaders = useAverage => [
  'ID',
  'Name',
  'Team',
  'Opponent',
  'Status',
  'Price',
  useAverage ? 'Average' : 'Projection',
  'SD',
  'Floor',
  'Ceiling',
  'Position',
  'Position2'
];

const MIN_PROJECTION = -10;
const MAX_PROJECTION = 9999;

const ImportExportContent = ({
  onUpdatePlayers,
  entities,
  isAFL,
  isNBA,
  sortDirection,
  sortField,
  onSetSortField,
  isNativeWebView,
  useAverage
}: Props) => {
  const [acceptedFile, setAcceptedFile] = useState(undefined);
  const [isFileRejected, setIsFileRejected] = useState(false);
  const [error, setError] = useState(undefined);
  const headers = getHeaders(useAverage);

  const handleDocumentDrop = (accepted: Array<Object>, rejected: Array<Object>) => {
    const file = head(accepted || []);

    setAcceptedFile(file);
    setIsFileRejected(!!head(rejected));

    setError(undefined);
  };

  const onDropCallback = useCallback(handleDocumentDrop, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ['.csv'],
    maxSize: 5000000,
    multiple: false,
    onDrop: onDropCallback
  });

  const handleImport = async () => {
    if (!acceptedFile) {
      return;
    }

    try {
      const data = await fileToText(acceptedFile);

      const { data: lines } = parse(data, { header: false }) || {};

      if (!Array.isArray(lines) || (head(lines) || '') === headers.join(',')) {
        setError('Invalid CSV file, please make sure the file was downloaded from this page.');
        return;
      }

      if (lines.length <= 1) {
        setError(`No data in CSV file.`);
        return;
      }

      const playersData = [];

      for (let i = 1; i < lines.length; i += 1) {
        const row = lines[i];

        if (row.length > 1) {
          const idRaw = toString(get('[0]', row) || '').trim();
          const projection = toString(get('[6]', row) || '').trim();
          const playerName = toString(get('[1]', row) || '').trim();
          const isTeam = idRaw.charAt(0) === 'T';
          const id = isTeam ? idRaw.substring(1) : idRaw;

          if (!id || isNaN(id)) {
            // eslint-disable-line
            setError(`Line ${i + 1} does not have a valid ID.`);
            return;
          }
          if (!playerName || playerName.length <= 3) {
            setError(`Line ${i + 1} does not have a valid player's name.`);
            return;
          }
          if (!projection || isNaN(projection)) {
            // eslint-disable-line
            setError(`Line ${i + 1} does not have a valid projection.`);
            return;
          }
          if (projection < MIN_PROJECTION) {
            setError(`Line ${i + 1}: projection is too small`);
            return;
          }
          if (projection > MAX_PROJECTION) {
            setError(`Line ${i + 1}: projection is too big`);
            return;
          }
          playersData.push({
            idStr: isTeam ? `TEAM_${id}` : `PLAYER_${id}`,
            insiderScoreUpdated: round(parseFloat(projection || '0'), 1)
          });
        }
      }

      onUpdatePlayers(keyBy('idStr', playersData));

      setAcceptedFile(undefined);
      if (sortField !== 'insiderScoreUpdated' || sortDirection !== 'desc') {
        onSetSortField('insiderScoreUpdated');
      }
    } catch (err) {
      setError(err);
    }
  };

  const handleExport = ({ withoutProjection = false } = {}) => {
    const stdDevVariation = isAFL || isNBA ? 2 : 1;
    const data = [headers];

    entities
      .filter(p => !!p.insiderId)
      .forEach(
        ({
          insiderId,
          preferredName,
          insiderTeam,
          insiderOpponent,
          insiderStatus,
          salary,
          insiderScore,
          insiderScoreUpdated,
          insiderStdDev,
          insiderPosition,
          insiderPosition2
        }) => {
          data.push([
            insiderId,
            preferredName,
            insiderTeam,
            insiderOpponent,
            insiderStatus,
            salary,
            withoutProjection ? '' : insiderScoreUpdated,
            insiderStdDev,
            round(insiderScore - insiderStdDev, 1),
            round(insiderScore + stdDevVariation * insiderStdDev, 1),
            insiderPosition,
            insiderPosition2
          ]);
        }
      );

    download(unparse(data), 'ProjectionTable.csv', 'text/csv');
  };

  const canImport = acceptedFile && !error;
  const scoreLabelPlural = useAverage ? 'averages' : 'projections';

  return (
    <div>
      <h3>Settings will update immediately upon changing the below fields</h3>
      <Columns>
        <Column className="is-half">
          {isNativeWebView ? (
            <p>Export functionality is currently not available within the app</p>
          ) : (
            <React.Fragment>
              <h3>Export</h3>
              <h3>Download with player {scoreLabelPlural}</h3>
              <p>
                Use this version to tweak or view our {scoreLabelPlural} online, or with your own
                lineup building algorithm.
              </p>
              <Button kind="success" onClick={handleExport}>
                Download CSV
              </Button>
              <hr />
              <h3>Download without player {scoreLabelPlural}</h3>
              <p>
                Use this version to import {scoreLabelPlural} from another source, and prepare them
                for use with the Lineup Cruncher.
              </p>
              <Button kind="success" onClick={() => handleExport({ withoutProjection: true })}>
                Download CSV
              </Button>
            </React.Fragment>
          )}
        </Column>
        <Column className="is-narrow">
          <Divider vertical />
        </Column>
        <Column className="is-half">
          <h3>Import</h3>
          <p>To import your own player {scoreLabelPlural} into the lineup cruncher:</p>
          <ol>
            <li>Download the file from &quot;Download without player {scoreLabelPlural}&quot;.</li>
            <li>
              Input your {scoreLabelPlural} into the empty column of the downloaded CSV. Do not
              change anything else in the CSV.
            </li>
            <li>
              Use the Browse prompt below to upload the file with your {scoreLabelPlural}, and click
              import.
            </li>
          </ol>
          <StyledDropzone {...getRootProps({ isDragActive })}>
            <input {...getInputProps()} />
            {acceptedFile ? (
              <DropzoneContent>
                <DropZoneTextContainer>{acceptedFile.name}</DropZoneTextContainer>
              </DropzoneContent>
            ) : (
              <DropZoneTextContainer>
                <div>
                  <DropZoneMainText>Click or drop files here</DropZoneMainText>
                  <ExtensionLabel>(csv)</ExtensionLabel>
                </div>
              </DropZoneTextContainer>
            )}
          </StyledDropzone>
          {isFileRejected && (
            <DropZoneRejectedText>
              Cannot upload this file. It is either too large or not an accepted file type.
            </DropZoneRejectedText>
          )}
          <Button kind="success" onClick={handleImport} disabled={!canImport}>
            Import
          </Button>
          {error && <FormValidationMessage kind="danger" message={error} />}
        </Column>
      </Columns>
      <NotificationHandler
        afterCond={!acceptedFile && !error}
        beforeCond={!!acceptedFile}
        type="success"
      >
        <span>
          Imported successfully
          <br />
        </span>
      </NotificationHandler>
    </div>
  );
};

ImportExportContent.defaultProps = defaultProps;

function mapStateToProps(state: GlobalState) {
  const entities = entitiesSelector(state, { isCruncher: true });
  return {
    entities: entities.filter(e => !!e.insiderId),
    isAFL: isChallengeAFL(state.challenge.competitionId),
    isNBA: isChallengeNBA(state.challenge.competitionId),
    sortField: state.challenge.sortField,
    sortDirection: state.challenge.sortDirection,
    isNativeWebView: state.ui.isNativeWebView
  };
}
const actions = {
  onUpdatePlayers: updatePlayers,
  onSetSortField: setSortField
};

export default connect(mapStateToProps, actions)(ImportExportContent);
